import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { useTimer } from 'use-timer';
import { useIsomorphicLayoutEffect } from 'framer-motion';
import useMediaQuery from '../custom-hooks/useMediaQuery';
import ButtonLink from '../shared/ButtonLinkComponent';
import TrendingTopicInsight from './TrendingTopicInsight';
import ServerStylesConditionalWrapper from '../shared/ServerStylesConditionalWrapper';
import { device } from '../../utils/styled-component-mixins';
import { withLocalization } from '../shared/Context/LocalizationContext';
import { useLocalization } from '../custom-hooks/useLocalization';
import useOnResize from '../custom-hooks/use-on-resize';

const TrendingTopicsWrapper = styled.div`
  margin-inline: auto;
  margin-top: 8.8rem;
  max-width: 109rem;

  @media ${device.desktop} {
    margin-top: 8.5rem;
  }

  h2 {
    font-size: 3rem;
    font-weight: 400;
    text-align: center;

    @media ${device.desktop} {
      font-size: 3.6rem;
    }
  }

  .topic-container {
    display: none;
  }

  .opened {
    display: grid;
    padding: 0 1.5rem;

    @media ${device.desktop} {
      grid-template-columns: 1fr 1fr;
      padding: 0;
    }
  }

  .pill-wrapper {
    align-items: center;
    display: flex;
    height: 6.8rem;
    margin: 40px auto;
    position: relative;

    @media ${device.desktop} {
      height: auto;
      margin: 0;
    }
  }

  .play-button,
  .pause-button {
    border: none;
    position: absolute;
    right: 0;
    top: 14rem;

    @media ${device.desktop} {
      bottom: auto;
      right: auto;
      top: 17rem;
    }
  }

  .pill-container {
    background-color: var(--light-grey);
    margin: 40px auto;
    overflow-x: auto;
    padding: 0 1.6rem;
    scrollbar-width: none; /* Firefox */
    white-space: nowrap;
    width: 100%;
    -ms-overflow-style: none; /* IE and Edge */

    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }

    @media ${device.desktop} {
      border-radius: 24px;
      padding: 0;
      position: static;
      width: max-content;
    }
  }

  .pill-item {
    border: none;
    color: var(--slate);
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1.8rem;
    margin-right: 0.8rem;
    padding: 1.5rem 1.6rem;

    &:last-child {
      margin-right: 0;
    }

    @media ${device.desktop} {
      font-size: 1.8rem;
      line-height: 2.4rem;
      padding: 1.2rem 1.6rem;
    }
  }

  .play-pause-wrapper {
    @media ${device.desktop} {
      margin-left: -6rem;
    }
  }

  .selected {
    background-color: var(--slate);
    border-radius: 24px;
    color: #ffffff;
  }

  .left-topic-container {
    display: flex;
    flex-direction: column;
    min-width: 100%;
  }

  .right-topic-container {
    margin-bottom: 4rem;

    .non-default-link {
      font-size: 1.8rem;
      margin-left: 3rem;

      @media ${device.desktop} {
        margin-left: auto;
      }
    }
  }

  .topic-image {
    filter: drop-shadow(0px 16px 80px rgba(91, 110, 127, 0.25));
    max-width: 535px;
    margin-bottom: 40px;
  }
`;

const getTallestTrendingTopic = (trendingTopicContainer, isDevice) => {
  const trendingTopicElements = trendingTopicContainer.querySelectorAll('.insight-panel > div');

  const firstTrendingTopicHeight = trendingTopicElements?.[0]?.getBoundingClientRect()?.height ?? 0;
  const tallestTrendingTopic = Array.from(trendingTopicElements).reduce((accumulator, element) => {
    const { height } = element.getBoundingClientRect();

    return Math.max(accumulator, height);
  }, 0);

  // eslint-disable-next-line no-param-reassign
  trendingTopicContainer.style.height = 'unset';

  requestAnimationFrame(() => {
    const rightTopicContainer = trendingTopicContainer.querySelector('.right-topic-container');
    const { height: trendingTopicContainerHeight } = trendingTopicContainer.getBoundingClientRect();
    const heightWithCurrentlyOpenTrendingTopic =
      firstTrendingTopicHeight === tallestTrendingTopic
        ? trendingTopicContainerHeight
        : trendingTopicContainerHeight + tallestTrendingTopic;
    const { height: rightTopicContainerHeight } = rightTopicContainer.getBoundingClientRect();
    const heightToSet = isDevice
      ? heightWithCurrentlyOpenTrendingTopic + rightTopicContainerHeight
      : heightWithCurrentlyOpenTrendingTopic;

    // eslint-disable-next-line no-param-reassign
    trendingTopicContainer.style.height = `${heightToSet}px`;
  });
};

const TrendingTopicsComponent = (props) => {
  const isDesktop = useMediaQuery(device.desktop);
  const { trendingTopics } = props;
  const { resourceStrings } = useLocalization();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedInsightIndex, setSelectedInsightIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const trendingTopicContainerRef = useRef(null);
  const buttonText = resourceStrings['bdO.FeaturedInsight.LinkText'];

  const { start, pause, reset } = useTimer({
    autostart: true,
    initialTime: 0,
    interval: 500,
    onTimeUpdate: (time) => {
      if (time === 13) {
        if (selectedInsightIndex === 2) {
          setSelectedInsightIndex(0);
        } else {
          setSelectedInsightIndex(selectedInsightIndex + 1);
        }
        reset();
        start();
      }
    },
    onTimeOver: () => {
      if (selectedInsightIndex === 2) {
        setSelectedInsightIndex(0);
      } else {
        setSelectedInsightIndex(selectedInsightIndex + 1);
      }
    }
  });

  const toggleAllVisibleInsightAnimationPlayState = (state) => {
    const openedTrendingTopics = document.querySelectorAll('.insight-progress-bar');

    openedTrendingTopics.forEach((topic) => {
      // eslint-disable-next-line no-param-reassign
      topic.style.animationPlayState = state;
    });
  };

  const changeSelectedIndex = (index) => {
    setSelectedInsightIndex(index);
    reset();
    start();

    if (isPaused) {
      toggleAllVisibleInsightAnimationPlayState('paused');
      pause();
    }
  };

  const isDevice = useMediaQuery(device.deviceOnly);

  useIsomorphicLayoutEffect(() => {
    requestAnimationFrame(() =>
      getTallestTrendingTopic(trendingTopicContainerRef.current, isDevice)
    );
  }, [selectedIndex]);

  useOnResize(() => getTallestTrendingTopic(trendingTopicContainerRef.current, isDevice), 200);

  useEffect(() => {
    if (isPaused) {
      pause();
      toggleAllVisibleInsightAnimationPlayState('paused');
    } else {
      start();
      toggleAllVisibleInsightAnimationPlayState('running');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaused]);

  useEffect(() => {
    reset();

    if (!isPaused) {
      start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  return (
    <ServerStylesConditionalWrapper>
      <TrendingTopicsWrapper>
        <div className="trending-topics-container" ref={trendingTopicContainerRef}>
          <h2>{props.trendingTopicsHeading}</h2>
          <div className="pill-wrapper">
            <div className="pill-container">
              {trendingTopics.map((pill, index) => (
                <button
                  key={index}
                  aria-expanded={index === selectedIndex}
                  aria-controls="insight-panel"
                  onClick={() => {
                    setSelectedIndex(index);
                    setSelectedInsightIndex(0);
                  }}
                  className={classNames('pill-item', { selected: index === selectedIndex })}
                >
                  {pill.meta.pageTitle}
                </button>
              ))}
            </div>
          </div>
          <div className="play-pause-wrapper">
            <button
              className={classNames(isPaused ? 'play-button' : 'pause-button')}
              aria-label={
                isPaused ? 'Switch between insights' : 'Pause automatic insight switching'
              }
              onClick={() => {
                setIsPaused((previousIsPaused) => !previousIsPaused);
              }}
            >
              <span className="material-icons-round">{isPaused ? 'play_arrow' : 'pause'}</span>
            </button>
          </div>
          {trendingTopics.map((topic, index) => (
            <div
              key={index}
              className={classNames('topic-container', { opened: index === selectedIndex })}
            >
              {!!topic?.insightsDetails && (
                <div className="left-topic-container">
                  {topic.insightsDetails.map((insight, topicIndex) => (
                    <TrendingTopicInsight
                      key={insight.title}
                      title={insight.title}
                      description={insight.description}
                      buttonText={buttonText}
                      buttonLink={`${insight.externalUrl ? insight.externalUrl : insight.url}`}
                      index={topicIndex}
                      primaryCategory={insight.primaryCategory}
                      selectedInsightIndex={selectedInsightIndex}
                      changeSelectedIndex={changeSelectedIndex}
                    />
                  ))}
                </div>
              )}
              <div className="right-topic-container">
                {isDesktop && (
                  <img className="topic-image" src={topic.image} alt={topic.imageAltText} />
                )}
                {topic.buttonText && topic.buttonLink && (
                  <ButtonLink text={topic.buttonText} link={topic.buttonLink} type="tertiary" />
                )}
              </div>
            </div>
          ))}
        </div>
      </TrendingTopicsWrapper>
    </ServerStylesConditionalWrapper>
  );
};

export default withLocalization(TrendingTopicsComponent);
